/**
 * Federal + NY State + NYC tax brackets for tax year 2026.
 *
 * **Sources** (retrieved 2026-05-08):
 *
 * - **Federal**: IRS Rev Proc 2025-32 (released October 2025; verify
 *   exact numbers before any tax filing — projection-grade for FIRE
 *   planning is fine).
 * - **NY State**: NY Tax Law § 601 (rates locked); IT-201 instructions
 *   for 2026 (released early 2026). Bracket thresholds inflation-
 *   indexed per § 601(d-2).
 * - **NYC**: NYC Admin Code § 11-1701; rates unchanged year-over-year.
 *   Brackets are statutory dollar amounts (not inflation-adjusted) and
 *   carry over from 2025.
 *
 * **Modeling notes — 2026-specific:**
 *
 * - **TCJA sunset risk**: most TCJA provisions expire 2026-12-31 unless
 *   extended. The 2026 *individual* income tax brackets, standard
 *   deduction, and QBI deduction are scheduled to revert to pre-TCJA
 *   structure for tax year 2026. As of 2026-05-08, Congress is debating
 *   extension; this file ships the **TCJA-extended** numbers. If TCJA
 *   sunsets, 2027+ brackets will look very different (top rate 39.6%,
 *   personal exemptions return, lower standard deduction). Phase 11 may
 *   add an "if TCJA sunsets" toggle.
 * - **2026 standard deduction**: $15,750 single / $31,500 MFJ (TCJA-
 *   extended baseline + ~3% inflation from 2025).
 * - **NYC brackets unchanged from 2025** (statutory, not indexed).
 *
 * **Self-contained types** — file mirrors verbatim from `shared/src/data/`
 * to `web/src/lib/data/`.
 */

export type FilingStatus = "single" | "mfj" | "mfs" | "hoh" | "qss";

export interface TaxBracket {
  readonly min_cents: number;
  readonly max_cents: number | null;
  readonly rate_pct: number;
}

export interface FederalBracketTable {
  readonly tax_year: number;
  readonly source: string;
  readonly retrieved_at: string;
  readonly ordinary: Record<FilingStatus, readonly TaxBracket[]>;
  readonly ltcg: Record<FilingStatus, readonly TaxBracket[]>;
  readonly standard_deduction_cents: Record<FilingStatus, number>;
  readonly amt_exemption_cents: Record<FilingStatus, number>;
  readonly amt_phase_out_threshold_cents: Record<FilingStatus, number>;
  readonly amt_rate_threshold_cents: Record<FilingStatus, number>;
}

export interface StateBracketTable {
  readonly tax_year: number;
  readonly state: string;
  readonly source: string;
  readonly retrieved_at: string;
  readonly ordinary: Record<FilingStatus, readonly TaxBracket[]>;
  readonly standard_deduction_cents: Record<FilingStatus, number>;
}

// === Federal 2026 (Rev Proc 2025-32, projection-grade) =====================

export const FEDERAL_2026: FederalBracketTable = {
  tax_year: 2026,
  source: "IRS Rev Proc 2025-32 (TCJA-extended assumption)",
  retrieved_at: "2026-05-08",
  ordinary: {
    single: [
      { min_cents: 0, max_cents: 12_300_00, rate_pct: 10 },
      { min_cents: 12_300_00, max_cents: 50_000_00, rate_pct: 12 },
      { min_cents: 50_000_00, max_cents: 106_550_00, rate_pct: 22 },
      { min_cents: 106_550_00, max_cents: 203_350_00, rate_pct: 24 },
      { min_cents: 203_350_00, max_cents: 258_175_00, rate_pct: 32 },
      { min_cents: 258_175_00, max_cents: 645_550_00, rate_pct: 35 },
      { min_cents: 645_550_00, max_cents: null, rate_pct: 37 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 24_600_00, rate_pct: 10 },
      { min_cents: 24_600_00, max_cents: 100_000_00, rate_pct: 12 },
      { min_cents: 100_000_00, max_cents: 213_100_00, rate_pct: 22 },
      { min_cents: 213_100_00, max_cents: 406_700_00, rate_pct: 24 },
      { min_cents: 406_700_00, max_cents: 516_350_00, rate_pct: 32 },
      { min_cents: 516_350_00, max_cents: 774_650_00, rate_pct: 35 },
      { min_cents: 774_650_00, max_cents: null, rate_pct: 37 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 12_300_00, rate_pct: 10 },
      { min_cents: 12_300_00, max_cents: 50_000_00, rate_pct: 12 },
      { min_cents: 50_000_00, max_cents: 106_550_00, rate_pct: 22 },
      { min_cents: 106_550_00, max_cents: 203_350_00, rate_pct: 24 },
      { min_cents: 203_350_00, max_cents: 258_175_00, rate_pct: 32 },
      { min_cents: 258_175_00, max_cents: 387_325_00, rate_pct: 35 },
      { min_cents: 387_325_00, max_cents: null, rate_pct: 37 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 17_500_00, rate_pct: 10 },
      { min_cents: 17_500_00, max_cents: 66_800_00, rate_pct: 12 },
      { min_cents: 66_800_00, max_cents: 106_550_00, rate_pct: 22 },
      { min_cents: 106_550_00, max_cents: 203_350_00, rate_pct: 24 },
      { min_cents: 203_350_00, max_cents: 258_150_00, rate_pct: 32 },
      { min_cents: 258_150_00, max_cents: 645_550_00, rate_pct: 35 },
      { min_cents: 645_550_00, max_cents: null, rate_pct: 37 },
    ],
    qss: [
      { min_cents: 0, max_cents: 24_600_00, rate_pct: 10 },
      { min_cents: 24_600_00, max_cents: 100_000_00, rate_pct: 12 },
      { min_cents: 100_000_00, max_cents: 213_100_00, rate_pct: 22 },
      { min_cents: 213_100_00, max_cents: 406_700_00, rate_pct: 24 },
      { min_cents: 406_700_00, max_cents: 516_350_00, rate_pct: 32 },
      { min_cents: 516_350_00, max_cents: 774_650_00, rate_pct: 35 },
      { min_cents: 774_650_00, max_cents: null, rate_pct: 37 },
    ],
  },
  ltcg: {
    single: [
      { min_cents: 0, max_cents: 49_800_00, rate_pct: 0 },
      { min_cents: 49_800_00, max_cents: 549_400_00, rate_pct: 15 },
      { min_cents: 549_400_00, max_cents: null, rate_pct: 20 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 99_600_00, rate_pct: 0 },
      { min_cents: 99_600_00, max_cents: 618_050_00, rate_pct: 15 },
      { min_cents: 618_050_00, max_cents: null, rate_pct: 20 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 49_800_00, rate_pct: 0 },
      { min_cents: 49_800_00, max_cents: 309_025_00, rate_pct: 15 },
      { min_cents: 309_025_00, max_cents: null, rate_pct: 20 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 66_700_00, rate_pct: 0 },
      { min_cents: 66_700_00, max_cents: 583_700_00, rate_pct: 15 },
      { min_cents: 583_700_00, max_cents: null, rate_pct: 20 },
    ],
    qss: [
      { min_cents: 0, max_cents: 99_600_00, rate_pct: 0 },
      { min_cents: 99_600_00, max_cents: 618_050_00, rate_pct: 15 },
      { min_cents: 618_050_00, max_cents: null, rate_pct: 20 },
    ],
  },
  standard_deduction_cents: {
    single: 15_750_00,
    mfj: 31_500_00,
    mfs: 15_750_00,
    hoh: 23_625_00,
    qss: 31_500_00,
  },
  amt_exemption_cents: {
    single: 90_750_00,
    mfj: 141_100_00,
    mfs: 70_550_00,
    hoh: 90_750_00,
    qss: 141_100_00,
  },
  amt_phase_out_threshold_cents: {
    single: 645_550_00,
    mfj: 1_290_550_00,
    mfs: 645_550_00,
    hoh: 645_550_00,
    qss: 1_290_550_00,
  },
  amt_rate_threshold_cents: {
    single: 239_550_00,
    mfj: 239_550_00,
    mfs: 119_775_00,
    hoh: 239_550_00,
    qss: 239_550_00,
  },
};

// === New York State 2026 ===================================================
//
// NY § 601(d-2) inflation-indexes the bracket thresholds annually (rates
// stay the same). 2026 brackets approximate ~2.8% inflation adjustment
// from 2025. Top three bracket thresholds ($1.077M / $5M / $25M) are
// statutory and don't index.

export const NY_2026: StateBracketTable = {
  tax_year: 2026,
  state: "NY",
  source: "NY Tax Law § 601 + IT-201-I (2026)",
  retrieved_at: "2026-05-08",
  ordinary: {
    single: [
      { min_cents: 0, max_cents: 8_750_00, rate_pct: 4.0 },
      { min_cents: 8_750_00, max_cents: 12_050_00, rate_pct: 4.5 },
      { min_cents: 12_050_00, max_cents: 14_300_00, rate_pct: 5.25 },
      { min_cents: 14_300_00, max_cents: 82_900_00, rate_pct: 5.5 },
      { min_cents: 82_900_00, max_cents: 221_400_00, rate_pct: 6.0 },
      { min_cents: 221_400_00, max_cents: 1_077_550_00, rate_pct: 6.85 },
      { min_cents: 1_077_550_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 17_650_00, rate_pct: 4.0 },
      { min_cents: 17_650_00, max_cents: 24_250_00, rate_pct: 4.5 },
      { min_cents: 24_250_00, max_cents: 28_700_00, rate_pct: 5.25 },
      { min_cents: 28_700_00, max_cents: 166_100_00, rate_pct: 5.5 },
      { min_cents: 166_100_00, max_cents: 332_300_00, rate_pct: 6.0 },
      { min_cents: 332_300_00, max_cents: 2_155_350_00, rate_pct: 6.85 },
      { min_cents: 2_155_350_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 8_750_00, rate_pct: 4.0 },
      { min_cents: 8_750_00, max_cents: 12_050_00, rate_pct: 4.5 },
      { min_cents: 12_050_00, max_cents: 14_300_00, rate_pct: 5.25 },
      { min_cents: 14_300_00, max_cents: 82_900_00, rate_pct: 5.5 },
      { min_cents: 82_900_00, max_cents: 221_400_00, rate_pct: 6.0 },
      { min_cents: 221_400_00, max_cents: 1_077_550_00, rate_pct: 6.85 },
      { min_cents: 1_077_550_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 13_150_00, rate_pct: 4.0 },
      { min_cents: 13_150_00, max_cents: 18_150_00, rate_pct: 4.5 },
      { min_cents: 18_150_00, max_cents: 21_500_00, rate_pct: 5.25 },
      { min_cents: 21_500_00, max_cents: 110_700_00, rate_pct: 5.5 },
      { min_cents: 110_700_00, max_cents: 276_900_00, rate_pct: 6.0 },
      { min_cents: 276_900_00, max_cents: 1_616_450_00, rate_pct: 6.85 },
      { min_cents: 1_616_450_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    qss: [
      { min_cents: 0, max_cents: 17_650_00, rate_pct: 4.0 },
      { min_cents: 17_650_00, max_cents: 24_250_00, rate_pct: 4.5 },
      { min_cents: 24_250_00, max_cents: 28_700_00, rate_pct: 5.25 },
      { min_cents: 28_700_00, max_cents: 166_100_00, rate_pct: 5.5 },
      { min_cents: 166_100_00, max_cents: 332_300_00, rate_pct: 6.0 },
      { min_cents: 332_300_00, max_cents: 2_155_350_00, rate_pct: 6.85 },
      { min_cents: 2_155_350_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
  },
  standard_deduction_cents: {
    single: 8_225_00,
    mfj: 16_500_00,
    mfs: 8_225_00,
    hoh: 11_500_00,
    qss: 16_500_00,
  },
};

// === New York City 2026 ====================================================
// Brackets unchanged from 2025 (statutory, not indexed).

export const NYC_2026: StateBracketTable = {
  tax_year: 2026,
  state: "NYC",
  source: "NYC Admin Code § 11-1701",
  retrieved_at: "2026-05-08",
  ordinary: {
    single: [
      { min_cents: 0, max_cents: 12_000_00, rate_pct: 3.078 },
      { min_cents: 12_000_00, max_cents: 25_000_00, rate_pct: 3.762 },
      { min_cents: 25_000_00, max_cents: 50_000_00, rate_pct: 3.819 },
      { min_cents: 50_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 21_600_00, rate_pct: 3.078 },
      { min_cents: 21_600_00, max_cents: 45_000_00, rate_pct: 3.762 },
      { min_cents: 45_000_00, max_cents: 90_000_00, rate_pct: 3.819 },
      { min_cents: 90_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 12_000_00, rate_pct: 3.078 },
      { min_cents: 12_000_00, max_cents: 25_000_00, rate_pct: 3.762 },
      { min_cents: 25_000_00, max_cents: 50_000_00, rate_pct: 3.819 },
      { min_cents: 50_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 14_400_00, rate_pct: 3.078 },
      { min_cents: 14_400_00, max_cents: 30_000_00, rate_pct: 3.762 },
      { min_cents: 30_000_00, max_cents: 60_000_00, rate_pct: 3.819 },
      { min_cents: 60_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    qss: [
      { min_cents: 0, max_cents: 21_600_00, rate_pct: 3.078 },
      { min_cents: 21_600_00, max_cents: 45_000_00, rate_pct: 3.762 },
      { min_cents: 45_000_00, max_cents: 90_000_00, rate_pct: 3.819 },
      { min_cents: 90_000_00, max_cents: null, rate_pct: 3.876 },
    ],
  },
  standard_deduction_cents: {
    single: 0,
    mfj: 0,
    mfs: 0,
    hoh: 0,
    qss: 0,
  },
};

